import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { TIMEZONES, type Timezone } from '@/shared/constants/timezones';

dayjs.extend(utc);
dayjs.extend(timezone);

const DEFAULT_DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';
const DEFAULT_TIME_FORMAT = 'HH:mm:ss';

/**
 * Formats a timestamp to a specified timezone
 * Note: Timestamps from trading platforms (MT4/MT5) are typically already in local time,
 * not UTC, so we don't apply UTC conversion before timezone conversion.
 * @param timestamp - Unix timestamp in milliseconds
 * @param timezoneKey - Target timezone (e.g., 'Europe/Rome', 'America/New_York')
 * @param format - Output format (default: 'YYYY-MM-DD HH:mm:ss')
 * @returns Formatted date string in the target timezone
 */
export const formatTimestampInTimezone = (
    timestamp: number,
    timezoneKey: string,
    format: string = DEFAULT_DATETIME_FORMAT,
): string => dayjs(timestamp).tz(timezoneKey).format(format);

/**
 * Formats a timestamp to time only in a specified timezone
 * @param timestamp - Unix timestamp in milliseconds
 * @param timezoneKey - Target timezone (e.g., 'Europe/Rome', 'America/New_York')
 * @returns Formatted time string (HH:mm:ss) in the target timezone
 */
export const formatTimeInTimezone = (
    timestamp: number,
    timezoneKey: string,
): string =>
    formatTimestampInTimezone(timestamp, timezoneKey, DEFAULT_TIME_FORMAT);

/**
 * Finds a timezone by its key
 * @param key - The timezone key to search for
 * @returns The timezone object or undefined if not found
 */
export const findTimezoneByKey = (key: string): Timezone | undefined =>
    TIMEZONES.find((tz) => tz.key === key);

/**
 * Extracts the numeric offset in hours from a timezone offset string
 * @param offsetString - The timezone offset string (e.g., 'UTC+2', 'UTC-4', 'UTC+3:30')
 * @returns The numeric offset in hours (e.g., 2, -4, 3.5)
 */
export const getTimezoneOffsetHours = (offsetString: string): number => {
    // If it's just 'UTC', the offset is 0
    if (offsetString === 'UTC') {
        return 0;
    }

    // Extract the sign and value from strings like 'UTC+2', 'UTC-4', 'UTC+3:30'
    const match = offsetString.match(/UTC([+-])(\d+)(?::(\d+))?/);

    if (!match || match.length < 3) {
        return 0;
    }

    const sign = match[1] === '+' ? 1 : -1;
    const hours = parseInt(match[2] || '0', 10);
    const minutes = match[3] ? parseInt(match[3], 10) : 0;

    return sign * (hours + minutes / 60);
};

/**
 * Converts a UTC time string to a local time based on the timezone offset
 * @param utcTimeString - The UTC time string in format 'HH:MM' or 'HH:MM:SS'
 * @param timezoneKey - The timezone key to use for conversion
 * @returns The converted time string in the same format as input
 */
export const convertUtcToTimezone = (
    utcTimeString: string,
    timezoneKey: string,
): string => {
    // Find the timezone by key
    const tzInfo = findTimezoneByKey(timezoneKey);

    if (!tzInfo) {
        return utcTimeString; // Return original if timezone not found
    }

    // Get the offset in hours
    const offsetHours = getTimezoneOffsetHours(tzInfo.offset);

    // Parse the time string
    const timeParts = utcTimeString.split(':');

    // Validate time parts
    if (timeParts.length < 2) {
        return utcTimeString; // Return original if format is invalid
    }

    const hours = parseInt(timeParts[0] || '0', 10);
    const minutes = parseInt(timeParts[1] || '0', 10);
    const seconds =
        timeParts.length > 2 ? parseInt(timeParts[2] || '0', 10) : 0;

    // Calculate the offset
    const totalMinutesOffset = offsetHours * 60;
    const totalMinutes = hours * 60 + minutes + totalMinutesOffset;

    // Calculate new hours and minutes
    let newHours = Math.floor(totalMinutes / 60) % 24;
    const newMinutes = totalMinutes % 60;

    // Handle negative hours
    if (newHours < 0) {
        newHours += 24;
    }

    // Format the result
    const formattedHours = newHours.toString().padStart(2, '0');
    const formattedMinutes = newMinutes.toString().padStart(2, '0');

    if (timeParts.length > 2) {
        const formattedSeconds = seconds.toString().padStart(2, '0');

        return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
    }

    return `${formattedHours}:${formattedMinutes}`;
};

// Financial timezones list - kept in sync with TimezoneSelector
const FINANCIAL_TIMEZONES = [
    // UTC+0 - London Financial District
    { key: 'UTC', label: 'UTC', offset: 'UTC+0' },
    { key: 'Europe/London', label: 'London', offset: 'UTC+0/+1' },

    // UTC+1/+2 - European Financial Centers
    { key: 'Europe/Frankfurt', label: 'Frankfurt', offset: 'UTC+1/+2' },
    { key: 'Europe/Paris', label: 'Paris', offset: 'UTC+1/+2' },
    { key: 'Europe/Zurich', label: 'Zurich', offset: 'UTC+1/+2' },
    { key: 'Europe/Rome', label: 'Milan', offset: 'UTC+1/+2' },

    // UTC+3 - Moscow Exchange
    { key: 'Europe/Moscow', label: 'Moscow', offset: 'UTC+3' },

    // UTC+4 - Dubai Financial Market
    { key: 'Asia/Dubai', label: 'Dubai', offset: 'UTC+4' },

    // UTC+8 - Asian Financial Hubs
    { key: 'Asia/Hong_Kong', label: 'Hong Kong', offset: 'UTC+8' },
    { key: 'Asia/Singapore', label: 'Singapore', offset: 'UTC+8' },
    { key: 'Asia/Shanghai', label: 'Shanghai', offset: 'UTC+8' },

    // UTC+9 - Tokyo Stock Exchange
    { key: 'Asia/Tokyo', label: 'Tokyo', offset: 'UTC+9' },

    // UTC+10/+11 - Australian Securities Exchange
    { key: 'Australia/Sydney', label: 'Sydney', offset: 'UTC+10/+11' },

    // UTC-5/-4 - Wall Street & North American Markets
    { key: 'America/New_York', label: 'New York', offset: 'UTC-5/-4' },
    { key: 'America/Chicago', label: 'Chicago', offset: 'UTC-6/-5' },
    { key: 'America/Los_Angeles', label: 'Los Angeles', offset: 'UTC-8/-7' },
];

/**
 * Get simplified timezone offset label
 */
export const getTimezoneLabel = (timezoneKey: string): string => {
    const timezoneData = FINANCIAL_TIMEZONES.find(
        (tz) => tz.key === timezoneKey,
    );

    if (!timezoneData || timezoneKey === 'UTC') {
        return '';
    }

    return timezoneData.offset;
};

/**
 * Convert a single time interval label to the target timezone
 * This function only converts the display label, not the actual data
 */
export const convertTimeIntervalLabel = (
    timeInterval: string,
    targetTimezone: string,
): string => {
    // If target timezone is UTC or undefined, return as is
    if (!targetTimezone || targetTimezone === 'UTC') {
        return timeInterval;
    }

    /*
    // Check if it's an hourly time format (HH:mm)
    const hourlyRegex = /^(\d{1,2}):(\d{2})$/;
    const hourlyMatch = timeInterval.match(hourlyRegex);
    
    if (hourlyMatch) {
        const hour = parseInt(hourlyMatch[1], 10);
        const minute = parseInt(hourlyMatch[2], 10);
        
        try {
            // Create a dayjs object for today at the specified UTC time
            const utcTime = dayjs().utc().hour(hour).minute(minute).second(0);
            
            // Convert to target timezone
            const targetTime = utcTime.tz(targetTimezone);
            
            // Format back to HH:mm
            return targetTime.format('HH:mm');
        } catch (error) {
            // If timezone conversion fails, return original
            return timeInterval;
        }
    } */

    // Check if it's a weekday name
    const weekdays = [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday',
    ];

    if (weekdays.includes(timeInterval)) {
        try {
            // For weekday conversion, we need to check if timezone shift would change the day
            // We'll use a reference time (e.g., Monday at 12:00 UTC) to see what day it becomes
            const weekdayIndex = weekdays.indexOf(timeInterval);
            const referenceDate = dayjs()
                .utc()
                .startOf('week')
                .add(weekdayIndex, 'day')
                .hour(12);
            const targetDate = referenceDate.tz(targetTimezone);

            // If the day changed, return the new day name
            return targetDate.format('dddd');
        } catch (error) {
            // If timezone conversion fails, return original
            return timeInterval;
        }
    }

    // For any other format, return as is
    return timeInterval;
};

/**
 * Interface for the temporal distribution data
 */
export interface TemporalDistributionData {
    timeInterval: string;
    counter: number;
    bullishPercentage: number;
    bearishPercentage: number;
    notVerifiedPercentage: number;
}

/**
 * Converts hourly time intervals from UTC to the specified timezone
 * @param data - Array of temporal distribution data with hourly intervals
 * @param targetTimezone - Target timezone (e.g., 'Europe/Rome', 'America/New_York')
 * @returns Array of temporal distribution data with converted time intervals
 */
export const convertHourlyDataToTimezone = (
    data: TemporalDistributionData[],
    targetTimezone: string,
): TemporalDistributionData[] => {
    if (!data || data.length === 0) {
        return data;
    }

    // If target timezone is UTC, return original data
    if (targetTimezone === 'UTC') {
        return data;
    }

    return data.map((item) => {
        const { timeInterval } = item;

        // Parse the time interval (e.g., "14:00") as UTC
        const utcTime = dayjs.utc(`2000-01-01T${timeInterval}:00`);

        // Convert to target timezone
        const convertedTime = utcTime.tz(targetTimezone);

        // Format back to HH:mm
        const convertedTimeInterval = convertedTime.format('HH:mm');

        return {
            ...item,
            timeInterval: convertedTimeInterval,
        };
    });
};

/**
 * Converts daily time intervals (weekdays) from UTC to the specified timezone
 * This function handles the case where a day might change due to timezone offset
 * @param data - Array of temporal distribution data with daily intervals
 * @param targetTimezone - Target timezone (e.g., 'Europe/Rome', 'America/New_York')
 * @returns Array of temporal distribution data with converted time intervals
 */
export const convertDailyDataToTimezone = (
    data: TemporalDistributionData[],
    targetTimezone: string,
): TemporalDistributionData[] => {
    if (!data || data.length === 0) {
        return data;
    }

    // If target timezone is UTC, return original data
    if (targetTimezone === 'UTC') {
        return data;
    }

    // Map weekday names to dayjs day numbers (0 = Sunday, 1 = Monday, etc.)
    const weekdayMap = new Map([
        ['Sunday', 0],
        ['Monday', 1],
        ['Tuesday', 2],
        ['Wednesday', 3],
        ['Thursday', 4],
        ['Friday', 5],
        ['Saturday', 6],
    ]);

    // Reverse map for converting back to names
    const dayNumberMap = new Map([
        [0, 'Sunday'],
        [1, 'Monday'],
        [2, 'Tuesday'],
        [3, 'Wednesday'],
        [4, 'Thursday'],
        [5, 'Friday'],
        [6, 'Saturday'],
    ]);

    return data.map((item) => {
        const { timeInterval } = item;

        // Check if it's a weekday name
        if (weekdayMap.has(timeInterval)) {
            const dayNumber = weekdayMap.get(timeInterval)!;

            // Create a reference date in UTC for the weekday
            // Use a date that we know the day of the week (e.g., 2000-01-02 was a Sunday)
            const referenceDate = dayjs.utc('2000-01-02').day(dayNumber);

            // Convert to target timezone at noon to avoid edge cases
            const convertedDate = referenceDate.hour(12).tz(targetTimezone);

            // Get the weekday name in the target timezone
            const convertedDayNumber = convertedDate.day();
            const convertedTimeInterval = dayNumberMap.get(convertedDayNumber)!;

            return {
                ...item,
                timeInterval: convertedTimeInterval,
            };
        }

        // If it's not a recognized weekday, return as-is
        return item;
    });
};

/**
 * Converts temporal distribution data to the specified timezone
 * Automatically detects if the data is hourly or daily and applies the appropriate conversion
 * @param data - Array of temporal distribution data
 * @param targetTimezone - Target timezone (e.g., 'Europe/Rome', 'America/New_York')
 * @returns Array of temporal distribution data with converted time intervals
 */
export const convertTemporalDistributionToTimezone = (
    data: TemporalDistributionData[],
    targetTimezone: string,
): TemporalDistributionData[] => {
    if (!data || data.length === 0) {
        return data;
    }

    // Check if the first item contains an hourly time interval (HH:mm format)
    const firstInterval = data[0]?.timeInterval;
    const isHourlyData = firstInterval
        ? /^\d{1,2}:\d{2}$/.test(firstInterval)
        : false;

    if (isHourlyData) {
        return convertHourlyDataToTimezone(data, targetTimezone);
    }

    // Otherwise, assume it's daily data
    return convertDailyDataToTimezone(data, targetTimezone);
};
